/**
 * i18n module — Sprint 20m foundation.
 *
 * Lightweight, no dependencies. Vanilla ES module.
 *
 * Usage:
 *   import { setLocale, t, applyTranslations } from '/public/js/i18n/index.js';
 *   await setLocale('ru');
 *   t('nav.clients') // "Клиенты"
 *   t('greeting', {name: 'Иван'}) // "Привет, Иван"
 *
 * In HTML:
 *   <span data-i18n="nav.clients"></span>
 *   <input data-i18n-attr="placeholder:search.placeholder">
 *
 * Locale resolution priority:
 *   1. Explicit setLocale(locale) call
 *   2. localStorage.locale
 *   3. user preference (window.MagellaniaUser?.preferred_language)
 *   4. <html lang="...">
 *   5. navigator.language → first match in available locales
 *   6. 'ru' (default)
 *
 * Locale files at /public/js/i18n/locales/{ru,en,es}.json
 */

const SUPPORTED = ['ru', 'en', 'es'];
const DEFAULT_LOCALE = 'ru';
const cache = {};
let currentLocale = DEFAULT_LOCALE;

function detectInitialLocale() {
    // 1. localStorage
    try {
        const stored = localStorage.getItem('locale');
        if (stored && SUPPORTED.includes(stored)) return stored;
    } catch (_) { /* ignore */ }

    // 2. user preference
    if (window.MagellaniaUser && window.MagellaniaUser.preferred_language
        && SUPPORTED.includes(window.MagellaniaUser.preferred_language)) {
        return window.MagellaniaUser.preferred_language;
    }

    // 3. <html lang="...">
    const htmlLang = document.documentElement.lang;
    if (htmlLang && SUPPORTED.includes(htmlLang.toLowerCase().slice(0, 2))) {
        return htmlLang.toLowerCase().slice(0, 2);
    }

    // 4. navigator.language
    const navLang = (navigator.language || 'ru').toLowerCase().slice(0, 2);
    if (SUPPORTED.includes(navLang)) return navLang;

    return DEFAULT_LOCALE;
}

/**
 * Sprint 25 perf: namespace-subset loading.
 *
 * Caller can declare upfront which namespaces it needs (e.g. login.html
 * needs only `login`, `common`, `btn`) by setting
 *   window.__I18N_NAMESPACES = ['login', 'common', 'btn'];
 * BEFORE importing this module. Then we hit the subset endpoint
 * /api/v1/i18n/<locale>?ns=login,common,btn which returns ~3KB вместо
 * ~100KB monolithic dict. Полезно для маленьких страниц (login, signup-org).
 *
 * Когда subset уже загружен и потом code обращается к ключу из не-загруженного
 * namespace — t() возвращает key as-is (graceful degradation, как раньше для
 * missing keys).
 */
async function loadLocale(locale) {
    if (cache[locale]) return cache[locale];
    const namespaces = (typeof window !== 'undefined' && Array.isArray(window.__I18N_NAMESPACES))
        ? window.__I18N_NAMESPACES.filter(Boolean)
        : null;
    const url = namespaces && namespaces.length
        ? `/api/v1/i18n/${locale}?ns=${encodeURIComponent(namespaces.join(','))}`
        : `/public/js/i18n/locales/${locale}.json`;
    try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('locale_not_found');
        const dict = await res.json();
        cache[locale] = dict;
        return dict;
    } catch (err) {
        console.warn(`[i18n] Failed to load ${locale}, fallback to ${DEFAULT_LOCALE}:`, err.message);
        if (locale !== DEFAULT_LOCALE) {
            return loadLocale(DEFAULT_LOCALE);
        }
        return {};
    }
}

export async function setLocale(locale) {
    if (!SUPPORTED.includes(locale)) {
        console.warn(`[i18n] Unsupported locale "${locale}", defaulting to ${DEFAULT_LOCALE}`);
        locale = DEFAULT_LOCALE;
    }
    await loadLocale(locale);
    currentLocale = locale;
    document.documentElement.lang = locale;
    try { localStorage.setItem('locale', locale); } catch (_) {}
    applyTranslations(document);
    document.dispatchEvent(new CustomEvent('i18n:locale-changed', { detail: { locale } }));
}

/**
 * Translate key. Supports nested keys (`nav.clients`) and parameter
 * interpolation (`greeting` → `Привет, {name}!`).
 *
 * Returns key if not found (graceful degradation).
 */
export function t(key, params = {}) {
    const dict = cache[currentLocale] || {};
    const parts = key.split('.');
    let value = dict;
    for (const p of parts) {
        if (value && typeof value === 'object' && p in value) {
            value = value[p];
        } else {
            return key;  // missing key — show key as fallback
        }
    }
    if (typeof value !== 'string') return key;
    return value.replace(/\{(\w+)\}/g, (m, name) =>
        Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : m
    );
}

/**
 * Apply translations to all `[data-i18n]` and `[data-i18n-attr]` elements
 * within scope.
 *
 * @param {HTMLElement|Document} scope
 */
export function applyTranslations(scope) {
    if (!scope || !scope.querySelectorAll) return;

    scope.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.dataset.i18n;
        if (!key) return;
        el.textContent = t(key);
    });

    scope.querySelectorAll('[data-i18n-attr]').forEach(el => {
        // Format: "attr:key,attr2:key2"
        const spec = el.dataset.i18nAttr;
        if (!spec) return;
        spec.split(',').forEach(pair => {
            const [attr, key] = pair.split(':').map(s => s.trim());
            if (attr && key) el.setAttribute(attr, t(key));
        });
    });
}

export function getLocale() {
    return currentLocale;
}

export function getSupportedLocales() {
    return [...SUPPORTED];
}

// Auto-init on import
const initial = detectInitialLocale();
setLocale(initial).catch(err => console.warn('[i18n] init failed:', err));

// Sprint 20m: expose to window for non-module consumers (shell.js renders
// sidebar before module load completes — fallback to label is safe).
if (typeof window !== 'undefined') {
    window.i18n = {
        t,
        setLocale,
        getLocale,
        getSupportedLocales,
        applyTranslations
    };
}
